/**
 * ╔══════════════════════════════════════════════════════════════════════════════════════════════╗
 * ║                            UPDATED DATABASE SERVICE v2.0                                    ║
 * ║                    Comprehensive Database Operations with New Schema                         ║
 * ╚══════════════════════════════════════════════════════════════════════════════════════════════╝
 */

import { supabase } from './supabase'
import type { User } from '@supabase/supabase-js'

// ===== TYPE DEFINITIONS =====

export interface UserProfile {
  id: string
  email: string
  first_name?: string
  last_name?: string
  phone?: string
  job_title?: string
  hourly_wage?: number
  pay_frequency?: 'hourly' | 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'annually'
  blur_earnings?: boolean
  share_location?: boolean
  is_active: boolean
  // Grace period tracking for email verification
  registration_date?: string
  grace_period_expires?: string
  email_verified?: boolean
  created_at: string
  updated_at: string
}

export interface UserRole {
  id: string
  user_id: string
  role_id: string
  role: Role
  assigned_at: string
  expires_at?: string
}

export interface Role {
  id: string
  name: string
  description?: string
  level: number
  can_access_admin: boolean
  can_manage_users: boolean
  can_approve_companies: boolean
  can_view_reports: boolean
  can_export_data: boolean
  can_access_premium_features: boolean
  subscription_tier: 'free' | 'basic' | 'premium' | 'enterprise'
  max_work_sessions?: number
  max_companies?: number
}

export interface Company {
  id: string
  name: string
  legal_name?: string
  website?: string
  industry?: string
  size_category?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise' | 'unknown'
  phone?: string
  email?: string
  is_union_friendly?: boolean
  has_collective_bargaining: boolean
  wage_transparency_grade?: string
  worker_satisfaction_score?: number
  approved_by?: string
  approved_at?: string
  approval_notes?: string
  is_active: boolean
  verification_status: string
  created_at: string
  updated_at: string
}

export interface CompanyLocation {
  id: string
  company_id: string
  company: Company
  address_line_1?: string
  address_line_2?: string
  city: string
  state: string
  zip_code?: string
  country: string
  latitude: number
  longitude: number
  location_type: 'headquarters' | 'branch' | 'warehouse' | 'store' | 'factory' | 'office' | 'remote'
  employee_count_estimate?: number
  is_24_hour: boolean
  average_wage_estimate?: number
  union_presence: boolean
  safety_rating?: number
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface ScraperCompany {
  id: string
  name: string
  website?: string
  industry?: string
  size_category?: string
  phone?: string
  email?: string
  headquarters_address?: string
  source_url?: string
  scraping_job_id?: string
  scraped_at: string
  data_quality_score?: number
  processing_status: 'pending' | 'processing' | 'completed' | 'failed'
  needs_review: boolean
  raw_data?: any
  created_at: string
}

export interface WorkSession {
  id: string
  user_id: string
  company_location_id?: string
  company_location?: CompanyLocation
  clock_in_time: string
  clock_out_time?: string
  total_duration?: string
  breaks?: any[]
  total_break_duration?: string
  clock_in_photo_url?: string
  clock_out_photo_url?: string
  clock_in_photo_hash?: string
  clock_out_photo_hash?: string
  clock_in_latitude?: number
  clock_in_longitude?: number
  clock_out_latitude?: number
  clock_out_longitude?: number
  location_verification_status: string
  session_signature?: string
  blockchain_hash?: string
  status: 'active' | 'completed' | 'disputed' | 'cancelled' | 'invalid'
  is_disputed: boolean
  overtime_eligible: boolean
  created_at: string
  updated_at: string
}

export interface UserReport {
  id: string
  user_id: string
  report_type: 'time_summary' | 'wage_analysis' | 'overtime_report' | 'dispute_evidence' | 'compliance_check'
  report_name?: string
  date_range_start?: string
  date_range_end?: string
  report_data?: any
  report_summary?: string
  is_exported: boolean
  export_format?: string
  export_url?: string
  sharing_token?: string
  generation_status: string
  generated_at: string
  expires_at?: string
  created_at: string
}

export interface DisputeCase {
  id: string
  user_id: string
  company_id?: string
  company?: Company
  case_title?: string
  dispute_type: 'wage_theft' | 'overtime_violation' | 'break_violation' | 'discrimination' | 'safety_violation' | 'other'
  description?: string
  evidence_work_sessions?: string[]
  supporting_documents?: string[]
  witness_statements?: string[]
  status: 'open' | 'investigating' | 'evidence_gathering' | 'legal_review' | 'resolved' | 'closed'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  resolution?: string
  resolution_date?: string
  outcome?: 'favorable' | 'unfavorable' | 'settled' | 'dismissed' | 'pending'
  legal_aid_organization?: string
  case_number?: string
  attorney_contact?: string
  created_at: string
  updated_at: string
}

// ===== DATABASE SERVICE CLASS =====

export class DatabaseService {
  private static instance: DatabaseService
  
  private constructor() {
    // Private constructor for singleton
  }
  
  public static getInstance(): DatabaseService {
    if (!DatabaseService.instance) {
      DatabaseService.instance = new DatabaseService()
    }
    return DatabaseService.instance
  }

  // ===== HEALTH CHECK =====
  
  async healthCheck(): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('count')
        .limit(1)
      
      if (error) {
        console.error('Database health check failed:', error)
        return false
      }
      
      console.log('✅ Database connection healthy')
      return true
    } catch (error) {
      console.error('Database health check failed:', error)
      return false
    }
  }

  // ===== USER MANAGEMENT =====
  
  async createUserProfile(user: User, additionalData?: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const userData = {
        id: user.id,
        email: user.email!,
        first_name: user.user_metadata?.first_name || additionalData?.first_name,
        last_name: user.user_metadata?.last_name || additionalData?.last_name,
        phone: user.phone || additionalData?.phone,
        ...additionalData
      }

      const { data, error } = await supabase
        .from('user_profiles')
        .insert([userData])
        .select()
        .single()

      if (error) {
        console.error('Failed to create user profile:', error)
        return null
      }

      // Assign default free_user role
      await this.assignUserRole(user.id, 'free_user')

      console.log('✅ User profile created successfully')
      return data
    } catch (error) {
      console.error('Error creating user profile:', error)
      return null
    }
  }

  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // User not found
        }
        console.error('Failed to get user profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error getting user profile:', error)
      return null
    }
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<UserProfile | null> {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', userId)
        .select()
        .single()

      if (error) {
        console.error('Failed to update user profile:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error updating user profile:', error)
      return null
    }
  }

  // ===== ROLE MANAGEMENT =====

  async getUserRoles(userId: string): Promise<UserRole[]> {
    try {
      const { data, error } = await supabase
        .from('user_role_assignments')
        .select(`
          *,
          role:user_roles(*)
        `)
        .eq('user_id', userId)
        .or('expires_at.is.null,expires_at.gt.now()')

      if (error) {
        console.error('Failed to get user roles:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting user roles:', error)
      return []
    }
  }

  async assignUserRole(userId: string, roleName: string, expiresAt?: string): Promise<boolean> {
    try {
      // First get the role ID
      const { data: role, error: roleError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('name', roleName)
        .single()

      if (roleError || !role) {
        console.error('Role not found:', roleName)
        return false
      }

      const { error } = await supabase
        .from('user_role_assignments')
        .insert([{
          user_id: userId,
          role_id: role.id,
          expires_at: expiresAt
        }])

      if (error) {
        console.error('Failed to assign user role:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error assigning user role:', error)
      return false
    }
  }

  async hasPermission(userId: string, permission: keyof Role): Promise<boolean> {
    try {
      const roles = await this.getUserRoles(userId)
      
      return roles.some(userRole => 
        userRole.role && userRole.role[permission] === true
      )
    } catch (error) {
      console.error('Error checking permission:', error)
      return false
    }
  }

  async isAdmin(userId: string): Promise<boolean> {
    return this.hasPermission(userId, 'can_access_admin')
  }

  async isSuperAdmin(userId: string): Promise<boolean> {
    try {
      const roles = await this.getUserRoles(userId)
      
      return roles.some(userRole => 
        userRole.role && userRole.role.name === 'super_admin'
      )
    } catch (error) {
      console.error('Error checking super admin:', error)
      return false
    }
  }

  // ===== COMPANY MANAGEMENT =====

  async getScraperCompanies(filters?: {
    needsReview?: boolean
    processingStatus?: string
    limit?: number
    offset?: number
  }): Promise<{ data: ScraperCompany[], total: number }> {
    try {
      let query = supabase
        .from('scraper_companies')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })

      if (filters?.needsReview !== undefined) {
        query = query.eq('needs_review', filters.needsReview)
      }

      if (filters?.processingStatus) {
        query = query.eq('processing_status', filters.processingStatus)
      }

      if (filters?.limit) {
        query = query.limit(filters.limit)
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
      }

      const { data, error, count } = await query

      if (error) {
        console.error('Failed to get scraper companies:', error)
        return { data: [], total: 0 }
      }

      return { data: data || [], total: count || 0 }
    } catch (error) {
      console.error('Error getting scraper companies:', error)
      return { data: [], total: 0 }
    }
  }

  async approveScraperCompany(scraperId: string, approvedBy: string, approvalData: Partial<Company>): Promise<Company | null> {
    try {
      // First get the scraper company data
      const { data: scraperCompany, error: scraperError } = await supabase
        .from('scraper_companies')
        .select('*')
        .eq('id', scraperId)
        .single()

      if (scraperError || !scraperCompany) {
        console.error('Scraper company not found:', scraperId)
        return null
      }

      // Create approved company
      const companyData = {
        name: scraperCompany.name,
        website: scraperCompany.website,
        industry: scraperCompany.industry,
        phone: scraperCompany.phone,
        email: scraperCompany.email,
        approved_by: approvedBy,
        approved_at: new Date().toISOString(),
        scraped_from_id: scraperId,
        is_active: true,
        verification_status: 'verified',
        ...approvalData
      }

      const { data: company, error } = await supabase
        .from('companies')
        .insert([companyData])
        .select()
        .single()

      if (error) {
        console.error('Failed to approve company:', error)
        return null
      }

      // Update scraper company status
      await supabase
        .from('scraper_companies')
        .update({ 
          needs_review: false, 
          processing_status: 'completed' 
        })
        .eq('id', scraperId)

      return company
    } catch (error) {
      console.error('Error approving company:', error)
      return null
    }
  }

  async bulkApproveScraperCompanies(scraperIds: string[], approvedBy: string): Promise<Company[]> {
    try {
      const approvedCompanies: Company[] = []
      
      for (const scraperId of scraperIds) {
        const company = await this.approveScraperCompany(scraperId, approvedBy, {})
        if (company) {
          approvedCompanies.push(company)
        }
      }

      return approvedCompanies
    } catch (error) {
      console.error('Error bulk approving companies:', error)
      return []
    }
  }

  async rejectScraperCompany(scraperId: string, reason?: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('scraper_companies')
        .update({ 
          needs_review: false, 
          processing_status: 'failed',
          // Could add rejection_reason field if needed
        })
        .eq('id', scraperId)

      if (error) {
        console.error('Failed to reject company:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error rejecting company:', error)
      return false
    }
  }

  async getApprovedCompanies(filters?: {
    industry?: string
    isActive?: boolean
    limit?: number
    offset?: number
  }): Promise<{ data: Company[], total: number }> {
    try {
      let query = supabase
        .from('companies')
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })

      if (filters?.industry) {
        query = query.eq('industry', filters.industry)
      }

      if (filters?.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive)
      }

      if (filters?.limit) {
        query = query.limit(filters.limit)
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
      }

      const { data, error, count } = await query

      if (error) {
        console.error('Failed to get approved companies:', error)
        return { data: [], total: 0 }
      }

      return { data: data || [], total: count || 0 }
    } catch (error) {
      console.error('Error getting approved companies:', error)
      return { data: [], total: 0 }
    }
  }

  // ===== WORK SESSION MANAGEMENT =====

  async clockIn(userId: string, companyLocationId?: string, photoUrl?: string, latitude?: number, longitude?: number): Promise<WorkSession | null> {
    try {
      // Check if user already has an active session
      const { data: existingSession } = await supabase
        .from('time_entries')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'active')
        .single()

      if (existingSession) {
        console.warn('User already has an active work session')
        return null
      }

      const sessionData = {
        user_id: userId,
        company_location_id: companyLocationId,
        clock_in_time: new Date().toISOString(),
        clock_in_photo_url: photoUrl,
        clock_in_latitude: latitude,
        clock_in_longitude: longitude,
        status: 'active' as const
      }

      const { data, error } = await supabase
        .from('time_entries')
        .insert([sessionData])
        .select(`
          *,
          company_location:company_locations(
            *,
            company:companies(*)
          )
        `)
        .single()

      if (error) {
        console.error('Failed to clock in:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error clocking in:', error)
      return null
    }
  }

  async clockOut(sessionId: string, photoUrl?: string, latitude?: number, longitude?: number): Promise<WorkSession | null> {
    try {
      const clockOutTime = new Date().toISOString()

      const { data, error } = await supabase
        .from('time_entries')
        .update({
          clock_out_time: clockOutTime,
          clock_out_photo_url: photoUrl,
          clock_out_latitude: latitude,
          clock_out_longitude: longitude,
          status: 'completed' as const
        })
        .eq('id', sessionId)
        .eq('status', 'active')
        .select(`
          *,
          company_location:company_locations(
            *,
            company:companies(*)
          )
        `)
        .single()

      if (error) {
        console.error('Failed to clock out:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error clocking out:', error)
      return null
    }
  }

  async getCurrentWorkSession(userId: string): Promise<WorkSession | null> {
    try {
      const { data, error } = await supabase
        .from('time_entries')
        .select(`
          *,
          company_location:company_locations(
            *,
            company:companies(*)
          )
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          return null // No active session
        }
        console.error('Failed to get current work session:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error getting current work session:', error)
      return null
    }
  }

  async getUserWorkSessions(userId: string, filters?: {
    startDate?: string
    endDate?: string
    status?: string
    limit?: number
    offset?: number
  }): Promise<{ data: WorkSession[], total: number }> {
    try {
      let query = supabase
        .from('time_entries')
        .select(`
          *,
          company_location:company_locations(
            *,
            company:companies(*)
          )
        `, { count: 'exact' })
        .eq('user_id', userId)
        .order('clock_in_time', { ascending: false })

      if (filters?.startDate) {
        query = query.gte('clock_in_time', filters.startDate)
      }

      if (filters?.endDate) {
        query = query.lte('clock_in_time', filters.endDate)
      }

      if (filters?.status) {
        query = query.eq('status', filters.status)
      }

      if (filters?.limit) {
        query = query.limit(filters.limit)
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1)
      }

      const { data, error, count } = await query

      if (error) {
        console.error('Failed to get user work sessions:', error)
        return { data: [], total: 0 }
      }

      return { data: data || [], total: count || 0 }
    } catch (error) {
      console.error('Error getting user work sessions:', error)
      return { data: [], total: 0 }
    }
  }

  // ===== REPORTING & ANALYTICS =====

  async generateUserReport(userId: string, reportType: UserReport['report_type'], config: {
    reportName?: string
    dateRangeStart?: string
    dateRangeEnd?: string
  }): Promise<UserReport | null> {
    try {
      // Generate report data based on type
      let reportData = {}
      let reportSummary = ''

      switch (reportType) {
        case 'time_summary':
          const { data: sessions } = await this.getUserWorkSessions(userId, {
            startDate: config.dateRangeStart,
            endDate: config.dateRangeEnd,
            status: 'completed'
          })
          
          const totalHours = sessions.reduce((sum, session) => {
            if (session.total_duration) {
              // Parse PostgreSQL interval to hours
              const hours = this.parseIntervalToHours(session.total_duration)
              return sum + hours
            }
            return sum
          }, 0)

          reportData = {
            totalSessions: sessions.length,
            totalHours: totalHours.toFixed(2),
            averageHoursPerSession: sessions.length > 0 ? (totalHours / sessions.length).toFixed(2) : 0,
            sessions: sessions
          }
          reportSummary = `Work summary: ${sessions.length} sessions, ${totalHours.toFixed(2)} total hours`
          break

        default:
          reportData = {}
          reportSummary = 'Report generated successfully'
      }

      const reportRecord = {
        user_id: userId,
        report_type: reportType,
        report_name: config.reportName || `${reportType}_${new Date().toISOString().split('T')[0]}`,
        date_range_start: config.dateRangeStart,
        date_range_end: config.dateRangeEnd,
        report_data: reportData,
        report_summary: reportSummary,
        generation_status: 'completed'
      }

      const { data, error } = await supabase
        .from('user_reports')
        .insert([reportRecord])
        .select()
        .single()

      if (error) {
        console.error('Failed to generate user report:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error generating user report:', error)
      return null
    }
  }

  private parseIntervalToHours(interval: string): number {
    // Parse PostgreSQL interval string to hours
    // Example: "08:30:00" -> 8.5
    try {
      const parts = interval.split(':')
      const hours = parseInt(parts[0]) || 0
      const minutes = parseInt(parts[1]) || 0
      const seconds = parseInt(parts[2]) || 0
      
      return hours + (minutes / 60) + (seconds / 3600)
    } catch {
      return 0
    }
  }

  async getUserReports(userId: string, limit = 10): Promise<UserReport[]> {
    try {
      const { data, error } = await supabase
        .from('user_reports')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Failed to get user reports:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting user reports:', error)
      return []
    }
  }

  // ===== DISPUTE MANAGEMENT =====

  async createDisputeCase(userId: string, disputeData: Partial<DisputeCase>): Promise<DisputeCase | null> {
    try {
      const caseData = {
        user_id: userId,
        status: 'open' as const,
        priority: 'medium' as const,
        ...disputeData
      }

      const { data, error } = await supabase
        .from('dispute_cases')
        .insert([caseData])
        .select(`
          *,
          company:companies(*)
        `)
        .single()

      if (error) {
        console.error('Failed to create dispute case:', error)
        return null
      }

      return data
    } catch (error) {
      console.error('Error creating dispute case:', error)
      return null
    }
  }

  async getUserDisputeCases(userId: string): Promise<DisputeCase[]> {
    try {
      const { data, error } = await supabase
        .from('dispute_cases')
        .select(`
          *,
          company:companies(*)
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Failed to get user dispute cases:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting user dispute cases:', error)
      return []
    }
  }

  // ===== STATISTICS =====

  async getScraperStats(): Promise<{
    total: number
    needsReview: number
    approved: number
    rejected: number
  }> {
    try {
      const [totalResult, needsReviewResult, approvedResult] = await Promise.all([
        supabase.from('scraper_companies').select('*', { count: 'exact', head: true }),
        supabase.from('scraper_companies').select('*', { count: 'exact', head: true }).eq('needs_review', true),
        supabase.from('companies').select('*', { count: 'exact', head: true })
      ])

      const total = totalResult.count || 0
      const needsReview = needsReviewResult.count || 0
      const approved = approvedResult.count || 0
      const rejected = total - needsReview - approved

      return {
        total,
        needsReview,
        approved,
        rejected
      }
    } catch (error) {
      console.error('Error getting scraper stats:', error)
      return { total: 0, needsReview: 0, approved: 0, rejected: 0 }
    }
  }

  async getComprehensiveScraperMetrics(): Promise<{
    overview: {
      totalScraped: number
      duplicatesFound: number
      uniqueRecords: number
      approvalRate: number
      errorRate: number
      processingRate: number
    }
    processingStatus: {
      pending: number
      processing: number
      completed: number
      failed: number
    }
    dataQuality: {
      highQuality: number    // >= 0.8
      mediumQuality: number  // 0.6-0.8
      lowQuality: number     // < 0.6
      noScore: number
      averageScore: number
    }
    timeMetrics: {
      last24Hours: number
      last7Days: number
      last30Days: number
      averagePerDay: number
    }
    errorAnalysis: {
      networkErrors: number
      parsingErrors: number
      validationErrors: number
      duplicateErrors: number
      unknownErrors: number
    }
    sourceBreakdown: {
      [source: string]: {
        total: number
        approved: number
        rejected: number
        avgQuality: number
      }
    }
    industryBreakdown: {
      [industry: string]: {
        total: number
        approved: number
        avgQuality: number
      }
    }
  }> {
    try {
      // Get comprehensive scraper data
      const { data: allScraperData, error } = await supabase
        .from('scraper_companies')
        .select('*')

      if (error) throw error

      const scraperData = allScraperData || []
      const now = new Date()
      const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000)
      const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
      const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

      // Get approved companies count
      const { count: approvedCount } = await supabase
        .from('companies')
        .select('*', { count: 'exact', head: true })

      // Calculate overview metrics
      const totalScraped = scraperData.length
      const uniqueCompanies = new Set(scraperData.map(c => c.name.toLowerCase().trim())).size
      const duplicatesFound = totalScraped - uniqueCompanies
      const approvalRate = totalScraped > 0 ? ((approvedCount || 0) / totalScraped) * 100 : 0
      
      // Processing status breakdown
      const processingStatus = {
        pending: scraperData.filter(c => c.processing_status === 'pending').length,
        processing: scraperData.filter(c => c.processing_status === 'processing').length,
        completed: scraperData.filter(c => c.processing_status === 'completed').length,
        failed: scraperData.filter(c => c.processing_status === 'failed').length
      }

      const errorRate = totalScraped > 0 ? (processingStatus.failed / totalScraped) * 100 : 0
      const processingRate = totalScraped > 0 ? (processingStatus.completed / totalScraped) * 100 : 0

      // Data quality analysis
      const qualityScores = scraperData
        .filter(c => c.data_quality_score !== null)
        .map(c => c.data_quality_score)

      const dataQuality = {
        highQuality: scraperData.filter(c => c.data_quality_score >= 0.8).length,
        mediumQuality: scraperData.filter(c => c.data_quality_score >= 0.6 && c.data_quality_score < 0.8).length,
        lowQuality: scraperData.filter(c => c.data_quality_score < 0.6 && c.data_quality_score !== null).length,
        noScore: scraperData.filter(c => c.data_quality_score === null).length,
        averageScore: qualityScores.length > 0 
          ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
          : 0
      }

      // Time-based metrics
      const last24HourCount = scraperData.filter(c => new Date(c.scraped_at) >= last24Hours).length
      const last7DayCount = scraperData.filter(c => new Date(c.scraped_at) >= last7Days).length
      const last30DayCount = scraperData.filter(c => new Date(c.scraped_at) >= last30Days).length

      const timeMetrics = {
        last24Hours: last24HourCount,
        last7Days: last7DayCount,
        last30Days: last30DayCount,
        averagePerDay: last30DayCount > 0 ? last30DayCount / 30 : 0
      }

      // Error analysis (based on processing status and raw data patterns)
      const failedRecords = scraperData.filter(c => c.processing_status === 'failed')
      const errorAnalysis = {
        networkErrors: failedRecords.filter(c => 
          c.raw_data?.error?.includes('network') || 
          c.raw_data?.error?.includes('timeout') ||
          c.raw_data?.error?.includes('connection')
        ).length,
        parsingErrors: failedRecords.filter(c => 
          c.raw_data?.error?.includes('parse') || 
          c.raw_data?.error?.includes('json') ||
          c.raw_data?.error?.includes('html')
        ).length,
        validationErrors: failedRecords.filter(c => 
          c.raw_data?.error?.includes('validation') || 
          c.raw_data?.error?.includes('schema')
        ).length,
        duplicateErrors: duplicatesFound,
        unknownErrors: Math.max(0, failedRecords.length - failedRecords.filter(c => c.raw_data?.error).length)
      }

      // Source breakdown
      const sourceBreakdown: { [source: string]: any } = {}
      const sources = [...new Set(scraperData.map(c => c.source_url || 'Unknown').filter(s => s))]
      
      for (const source of sources) {
        const sourceData = scraperData.filter(c => (c.source_url || 'Unknown') === source)
        const { count: sourceApproved } = await supabase
          .from('companies')
          .select('*', { count: 'exact', head: true })
          .eq('scraped_from_id', sourceData.map(s => s.id))

        const qualityScores = sourceData
          .filter(c => c.data_quality_score !== null)
          .map(c => c.data_quality_score)

        sourceBreakdown[source] = {
          total: sourceData.length,
          approved: sourceApproved || 0,
          rejected: sourceData.filter(c => c.processing_status === 'failed').length,
          avgQuality: qualityScores.length > 0 
            ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
            : 0
        }
      }

      // Industry breakdown
      const industryBreakdown: { [industry: string]: any } = {}
      const industries = [...new Set(scraperData.map(c => c.industry || 'Unknown').filter(i => i))]
      
      for (const industry of industries) {
        const industryData = scraperData.filter(c => (c.industry || 'Unknown') === industry)
        const { count: industryApproved } = await supabase
          .from('companies')
          .select('*', { count: 'exact', head: true })
          .eq('industry', industry)

        const qualityScores = industryData
          .filter(c => c.data_quality_score !== null)
          .map(c => c.data_quality_score)

        industryBreakdown[industry] = {
          total: industryData.length,
          approved: industryApproved || 0,
          avgQuality: qualityScores.length > 0 
            ? qualityScores.reduce((sum, score) => sum + score, 0) / qualityScores.length 
            : 0
        }
      }

      return {
        overview: {
          totalScraped,
          duplicatesFound,
          uniqueRecords: uniqueCompanies,
          approvalRate: Math.round(approvalRate * 100) / 100,
          errorRate: Math.round(errorRate * 100) / 100,
          processingRate: Math.round(processingRate * 100) / 100
        },
        processingStatus,
        dataQuality: {
          ...dataQuality,
          averageScore: Math.round(dataQuality.averageScore * 1000) / 1000
        },
        timeMetrics: {
          ...timeMetrics,
          averagePerDay: Math.round(timeMetrics.averagePerDay * 100) / 100
        },
        errorAnalysis,
        sourceBreakdown,
        industryBreakdown
      }
    } catch (error) {
      console.error('Error getting comprehensive scraper metrics:', error)
      
      // Return empty metrics structure
      return {
        overview: {
          totalScraped: 0,
          duplicatesFound: 0,
          uniqueRecords: 0,
          approvalRate: 0,
          errorRate: 0,
          processingRate: 0
        },
        processingStatus: { pending: 0, processing: 0, completed: 0, failed: 0 },
        dataQuality: { highQuality: 0, mediumQuality: 0, lowQuality: 0, noScore: 0, averageScore: 0 },
        timeMetrics: { last24Hours: 0, last7Days: 0, last30Days: 0, averagePerDay: 0 },
        errorAnalysis: { networkErrors: 0, parsingErrors: 0, validationErrors: 0, duplicateErrors: 0, unknownErrors: 0 },
        sourceBreakdown: {},
        industryBreakdown: {}
      }
    }
  }

  // ===== GRACE PERIOD MANAGEMENT =====

  /**
   * Set grace period for user (stores in database for persistence)
   */
  async setGracePeriod(userId: string, gracePeriodExpires: string): Promise<void> {
    try {
      await supabase
        .from('user_profiles')
        .upsert({
          id: userId,
          registration_date: new Date().toISOString(),
          grace_period_expires: gracePeriodExpires,
          email_verified: false
        }, {
          onConflict: 'id'
        });

      console.log('✅ Grace period set in database for user:', userId);
    } catch (error) {
      console.error('❌ Failed to set grace period in database:', error);
      throw error;
    }
  }

  /**
   * Check if user is in grace period (checks database)
   */
  async isUserInGracePeriod(userId: string): Promise<boolean> {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('grace_period_expires, email_verified')
        .eq('id', userId)
        .single();

      if (profile?.grace_period_expires && !profile.email_verified) {
        const graceExpiry = new Date(profile.grace_period_expires);
        return new Date() < graceExpiry;
      }

      return false;
    } catch (error) {
      console.error('❌ Failed to check grace period:', error);
      return false;
    }
  }

  /**
   * Mark user email as verified (ends grace period)
   */
  async markEmailVerified(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_profiles')
        .update({ 
          email_verified: true,
          grace_period_expires: null // Clear grace period
        })
        .eq('id', userId);

      console.log('✅ Email marked as verified for user:', userId);
    } catch (error) {
      console.error('❌ Failed to mark email as verified:', error);
      throw error;
    }
  }

  /**
   * Reset grace period for returning user (handles abandonment recovery)
   * Used when user returns after extended absence and grace period has expired
   */
  async resetGracePeriod(userId: string): Promise<void> {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('grace_period_reset_count, last_grace_period_reset')
        .eq('user_id', userId)
        .single();

      const resetCount = (profile?.grace_period_reset_count || 0) + 1;
      const newGracePeriod = new Date();
      newGracePeriod.setDate(newGracePeriod.getDate() + 7); // 7 more days

      await supabase
        .from('user_profiles')
        .update({
          grace_period_expires: newGracePeriod.toISOString(),
          grace_period_reset_count: resetCount,
          last_grace_period_reset: new Date().toISOString(),
          email_verified: false // Reset verification status
        })
        .eq('user_id', userId);

      console.log(`✅ Grace period reset for user: ${userId} (Reset #${resetCount})`);
    } catch (error) {
      console.error('❌ Failed to reset grace period:', error);
      throw error;
    }
  }

  /**
   * Check if user needs grace period reset on login
   * Returns true if grace period has expired and user should get a new one
   */
  async shouldResetGracePeriod(userId: string): Promise<boolean> {
    try {
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('grace_period_expires, email_verified, grace_period_reset_count, last_grace_period_reset')
        .eq('user_id', userId)
        .single();

      if (!profile) return false;

      // If email is verified, no grace period needed
      if (profile.email_verified) return false;

      // If no grace period set, user needs one
      if (!profile.grace_period_expires) return true;

      // If grace period has expired, user needs a reset
      const graceExpiry = new Date(profile.grace_period_expires);
      const hasExpired = new Date() > graceExpiry;

      if (hasExpired) {
        // Check if they've had too many resets (optional limit)
        const resetCount = profile.grace_period_reset_count || 0;
        if (resetCount >= 3) {
          console.warn(`⚠️ User ${userId} has reached maximum grace period resets (${resetCount})`);
          return false; // Block further resets
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error('❌ Failed to check if grace period reset needed:', error);
      return false;
    }
  }
}

export default DatabaseService
